/**
 * Gerador de orçamento em PDF — Miabeli
 *
 * Monta o arquivo PDF byte a byte, sem biblioteca externa: nada é baixado de CDN
 * e o script só faz trabalho quando o cliente clica em "Gerar orçamento".
 *
 * Usado em dois lugares:
 *   - página de produto  → snippets/v-orcamento.liquid
 *   - carrinho em gaveta → snippets/v-orcamento-carrinho.liquid
 */

/* ── unidades e layout ── */

const PT_POR_MM = 72 / 25.4;
const mm = (v) => v * PT_POR_MM;

const PAGINA = { largura: mm(210), altura: mm(297) };

const LAYOUT = {
  margemEsquerda: mm(15),
  margemDireita: mm(195),
  linhaEsquerda: mm(10),
  linhaDireita: mm(200),
  topo: mm(282),
  base: mm(20),
};

/* ── codificação de texto (WinAnsi) ── */

// Caracteres acima de U+00FF que o WinAnsiEncoding representa em 0x80–0x9F.
const WIN_ANSI_ALTO = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85,
  0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a,
  0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92,
  0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
  0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c,
  0x017e: 0x9e, 0x0178: 0x9f,
};

function paraWinAnsi(texto) {
  const bytes = [];
  for (const ch of String(texto)) {
    const cp = ch.codePointAt(0);
    if (cp < 0x100) bytes.push(cp);
    else if (WIN_ANSI_ALTO[cp]) bytes.push(WIN_ANSI_ALTO[cp]);
    else bytes.push(0x3f); // "?"
  }
  return bytes;
}

function latin1(str) {
  const out = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) out[i] = str.charCodeAt(i) & 0xff;
  return out;
}

/* ── larguras das fontes base-14 (Helvetica) ── */

// Largura por 1000 unidades de em, para os códigos 32–126.
const LARGURAS_NORMAL = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
  1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
  333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
  556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
];

const LARGURAS_NEGRITO = [
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
  975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
  333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
  611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584,
];

/**
 * Largura de um texto em pontos. Letras acentuadas têm, na Helvetica, a mesma
 * largura da letra base — por isso a normalização NFD antes da consulta.
 */
function larguraTexto(texto, tamanho, negrito) {
  const tabela = negrito ? LARGURAS_NEGRITO : LARGURAS_NORMAL;
  let total = 0;
  for (const ch of String(texto)) {
    const base = ch.normalize('NFD')[0];
    const cp = base.codePointAt(0);
    if (cp === 0xa0) total += tabela[0]; // espaço não-separável
    else if (cp >= 32 && cp <= 126) total += tabela[cp - 32];
    else total += tabela[0];
  }
  return (total / 1000) * tamanho;
}

function quebrarLinhas(texto, tamanho, negrito, larguraMax) {
  const palavras = String(texto).split(/\s+/).filter(Boolean);
  const linhas = [];
  let atual = '';
  for (const palavra of palavras) {
    const teste = atual ? `${atual} ${palavra}` : palavra;
    if (larguraTexto(teste, tamanho, negrito) <= larguraMax || !atual) {
      atual = teste;
    } else {
      linhas.push(atual);
      atual = palavra;
    }
  }
  if (atual) linhas.push(atual);
  return linhas;
}

/* ── página / conteúdo ── */

class Pagina {
  constructor() {
    this.ops = [];
    this.imagens = new Set();
  }

  #escrever(str) {
    this.ops.push(latin1(str));
  }

  texto(x, y, conteudo, { tamanho = 12, negrito = false, cinza = 0 } = {}) {
    const bytes = paraWinAnsi(conteudo);
    const escapados = [];
    for (const b of bytes) {
      if (b === 0x28 || b === 0x29 || b === 0x5c) escapados.push(0x5c);
      escapados.push(b);
    }
    this.#escrever(`BT /${negrito ? 'F2' : 'F1'} ${tamanho} Tf ${cinza} g ${x.toFixed(2)} ${y.toFixed(2)} Td (`);
    this.ops.push(new Uint8Array(escapados));
    this.#escrever(') Tj ET\n');
  }

  textoDireita(xDireita, y, conteudo, opcoes = {}) {
    const largura = larguraTexto(conteudo, opcoes.tamanho ?? 12, opcoes.negrito ?? false);
    this.texto(xDireita - largura, y, conteudo, opcoes);
  }

  linha(x1, y1, x2, y2, { espessura = 0.567, cinza = 0 } = {}) {
    this.#escrever(
      `${espessura} w ${cinza} G ${x1.toFixed(2)} ${y1.toFixed(2)} m ${x2.toFixed(2)} ${y2.toFixed(2)} l S\n`
    );
  }

  imagem(nome, x, y, largura, altura) {
    this.imagens.add(nome);
    this.#escrever(
      `q ${largura.toFixed(2)} 0 0 ${altura.toFixed(2)} ${x.toFixed(2)} ${y.toFixed(2)} cm /${nome} Do Q\n`
    );
  }

  bytes() {
    let total = 0;
    for (const parte of this.ops) total += parte.length;
    const saida = new Uint8Array(total);
    let pos = 0;
    for (const parte of this.ops) {
      saida.set(parte, pos);
      pos += parte.length;
    }
    return saida;
  }
}

/* ── serialização do PDF ── */

function concatenar(partes) {
  let total = 0;
  for (const p of partes) total += p.length;
  const saida = new Uint8Array(total);
  let pos = 0;
  for (const p of partes) {
    saida.set(p, pos);
    pos += p.length;
  }
  return saida;
}

/**
 * @param {Pagina[]} paginas
 * @param {Record<string, {bytes: Uint8Array, largura: number, altura: number, cor: string}>} imagens
 */
function montarPdf(paginas, imagens) {
  const corpos = [];
  const novoObjeto = (bytes) => {
    corpos.push(bytes);
    return corpos.length; // ids começam em 1
  };

  const idCatalogo = novoObjeto(latin1('')); // 1 — preenchido depois
  const idPaginas = novoObjeto(latin1('')); // 2 — preenchido depois
  const idFonteNormal = novoObjeto(
    latin1('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>')
  );
  const idFonteNegrito = novoObjeto(
    latin1('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>')
  );

  const idsImagens = {};
  for (const [nome, img] of Object.entries(imagens)) {
    const cabecalho = latin1(
      `<< /Type /XObject /Subtype /Image /Width ${img.largura} /Height ${img.altura}` +
        ` /ColorSpace /${img.cor} /BitsPerComponent 8 /Filter /DCTDecode /Length ${img.bytes.length} >>\nstream\n`
    );
    idsImagens[nome] = novoObjeto(concatenar([cabecalho, img.bytes, latin1('\nendstream')]));
  }

  const idsPaginas = [];
  for (const pagina of paginas) {
    const conteudo = pagina.bytes();
    const idConteudo = novoObjeto(
      concatenar([latin1(`<< /Length ${conteudo.length} >>\nstream\n`), conteudo, latin1('\nendstream')])
    );

    const recursosImagem = [...pagina.imagens]
      .filter((nome) => idsImagens[nome])
      .map((nome) => `/${nome} ${idsImagens[nome]} 0 R`)
      .join(' ');

    idsPaginas.push(
      novoObjeto(
        latin1(
          `<< /Type /Page /Parent ${idPaginas} 0 R` +
            ` /MediaBox [0 0 ${PAGINA.largura.toFixed(2)} ${PAGINA.altura.toFixed(2)}]` +
            ` /Resources << /Font << /F1 ${idFonteNormal} 0 R /F2 ${idFonteNegrito} 0 R >>` +
            (recursosImagem ? ` /XObject << ${recursosImagem} >>` : '') +
            ` >> /Contents ${idConteudo} 0 R >>`
        )
      )
    );
  }

  corpos[idCatalogo - 1] = latin1(`<< /Type /Catalog /Pages ${idPaginas} 0 R >>`);
  corpos[idPaginas - 1] = latin1(
    `<< /Type /Pages /Kids [${idsPaginas.map((id) => `${id} 0 R`).join(' ')}] /Count ${idsPaginas.length} >>`
  );

  const partes = [];
  let tamanho = 0;
  const adicionar = (bytes) => {
    partes.push(bytes);
    tamanho += bytes.length;
  };

  adicionar(latin1('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n'));

  const deslocamentos = [];
  corpos.forEach((corpo, i) => {
    deslocamentos[i] = tamanho;
    adicionar(latin1(`${i + 1} 0 obj\n`));
    adicionar(corpo);
    adicionar(latin1('\nendobj\n'));
  });

  const inicioXref = tamanho;
  adicionar(latin1(`xref\n0 ${corpos.length + 1}\n0000000000 65535 f \n`));
  for (const deslocamento of deslocamentos) {
    adicionar(latin1(`${String(deslocamento).padStart(10, '0')} 00000 n \n`));
  }
  adicionar(
    latin1(
      `trailer\n<< /Size ${corpos.length + 1} /Root ${idCatalogo} 0 R >>\nstartxref\n${inicioXref}\n%%EOF\n`
    )
  );

  return concatenar(partes);
}

/* ── imagens ── */

/** Lê largura, altura e nº de componentes direto dos marcadores SOF do JPEG. */
function lerDimensoesJpeg(bytes) {
  let i = 2; // pula SOI
  while (i < bytes.length) {
    if (bytes[i] !== 0xff) {
      i++;
      continue;
    }
    const marcador = bytes[i + 1];
    // SOF0–SOF15, exceto DHT (c4), DAC (cc) e RSTn (d0–d7)
    if (marcador >= 0xc0 && marcador <= 0xcf && marcador !== 0xc4 && marcador !== 0xcc) {
      const altura = (bytes[i + 5] << 8) | bytes[i + 6];
      const largura = (bytes[i + 7] << 8) | bytes[i + 8];
      const componentes = bytes[i + 9];
      const cor = componentes === 1 ? 'DeviceGray' : componentes === 4 ? 'DeviceCMYK' : 'DeviceRGB';
      return { largura, altura, cor };
    }
    const tamanho = (bytes[i + 2] << 8) | bytes[i + 3];
    i += 2 + tamanho;
  }
  return null;
}

async function baixarJpeg(url) {
  if (!url) return null;
  try {
    const resposta = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!resposta.ok) return null;
    const bytes = new Uint8Array(await resposta.arrayBuffer());
    const dimensoes = lerDimensoesJpeg(bytes);
    if (!dimensoes) return null;
    return { bytes, ...dimensoes };
  } catch {
    return null;
  }
}

function bytesDoCanvas(canvas) {
  const base64 = canvas.toDataURL('image/jpeg', 0.92).split(',')[1];
  const binario = atob(base64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  const dimensoes = lerDimensoesJpeg(bytes);
  return dimensoes ? { bytes, ...dimensoes } : null;
}

/** Rasteriza um elemento (canvas, img ou SVG) num quadrado, sobre fundo branco. */
function paraJpeg(fonte, lado) {
  const canvas = document.createElement('canvas');
  canvas.width = lado;
  canvas.height = lado;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, lado, lado);
  ctx.drawImage(fonte, 0, 0, lado, lado);
  return bytesDoCanvas(canvas);
}

/**
 * JPEG a partir de qualquer URL de imagem.
 *
 * Tenta primeiro baixar os bytes direto — caminho rápido, preserva a qualidade
 * original. Se não vier um JPEG (a CDN da Shopify devolve PNG quando o arquivo
 * tem transparência, mesmo pedindo format: 'jpg'), rasteriza no canvas sobre
 * fundo branco, mantendo a proporção.
 */
async function obterJpeg(url, larguraMax = 700) {
  if (!url) return null;

  const direto = await baixarJpeg(url);
  if (direto) return direto;

  try {
    const img = await carregarImagem(url);
    const escala = Math.min(1, larguraMax / img.naturalWidth);
    const largura = Math.max(1, Math.round(img.naturalWidth * escala));
    const altura = Math.max(1, Math.round(img.naturalHeight * escala));

    const canvas = document.createElement('canvas');
    canvas.width = largura;
    canvas.height = altura;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, largura, altura);
    ctx.drawImage(img, 0, 0, largura, altura);
    return bytesDoCanvas(canvas);
  } catch {
    return null;
  }
}

function carregarImagem(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function svgParaJpeg(svg, lado) {
  try {
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    return paraJpeg(await carregarImagem(url), lado);
  } catch {
    return null;
  }
}

const SVG_WHATSAPP =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="96" height="96">' +
  '<path fill="#111111" d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.15c-1.52 0-3.01-.41-4.31-1.18l-.31-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.35c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.83 2.41a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.47c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29z"/></svg>';

/** Reaproveita o gerador de QR já presente no tema; falha em silêncio. */
async function gerarQrJpeg(texto, lado) {
  try {
    const { QRCode } = await import('@theme/qr-code-generator');
    const caixa = document.createElement('div');
    caixa.style.cssText = 'position:absolute;left:-9999px;top:0;';
    document.body.appendChild(caixa);
    new QRCode(caixa, { text: texto, width: lado, height: lado, alt: 'QR code' });

    const alvo = await new Promise((resolve) => {
      let tentativas = 0;
      const procurar = () => {
        const canvas = caixa.querySelector('canvas');
        if (canvas) return resolve(canvas);
        const img = caixa.querySelector('img');
        if (img && img.src) {
          if (img.complete && img.naturalWidth) return resolve(img);
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
          return;
        }
        if (++tentativas > 40) return resolve(null);
        setTimeout(procurar, 25);
      };
      procurar();
    });

    const resultado = alvo ? paraJpeg(alvo, lado) : null;
    caixa.remove();
    return resultado;
  } catch {
    return null;
  }
}

/* ── formatação ── */

const formatadorBrl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const dinheiro = (centavos) => formatadorBrl.format(centavos / 100);

/** O tema guarda o número no formato do wa.me (5575999999999); aqui vira legível. */
function formatarWhatsapp(bruto) {
  const digitos = String(bruto || '').replace(/\D/g, '');
  if (!digitos) return '';
  const nacional = digitos.length > 11 && digitos.startsWith('55') ? digitos.slice(2) : digitos;
  if (nacional.length === 11) {
    return `(${nacional.slice(0, 2)}) ${nacional.slice(2, 7)}-${nacional.slice(7)}`;
  }
  if (nacional.length === 10) {
    return `(${nacional.slice(0, 2)}) ${nacional.slice(2, 6)}-${nacional.slice(6)}`;
  }
  return digitos;
}

/**
 * Link do wa.me com a mensagem já digitada para o cliente.
 *
 * O texto entra no QR code, então precisa ser curto: cada acento vira três
 * caracteres depois do encode e um QR muito denso fica difícil de ler numa
 * folha impressa. Sem número configurado, devolve null e quem chama decide o
 * que fazer.
 */
function linkWhatsapp(digitos, mensagem) {
  if (!digitos) return null;
  return `https://wa.me/${digitos}?text=${encodeURIComponent(mensagem)}`;
}

/** Corta um título comprido sem quebrar palavra no meio. */
function encurtar(texto, limite) {
  const limpo = String(texto || '').trim();
  if (limpo.length <= limite) return limpo;
  const corte = limpo.slice(0, limite);
  const ultimoEspaco = corte.lastIndexOf(' ');
  return `${(ultimoEspaco > limite * 0.6 ? corte.slice(0, ultimoEspaco) : corte).trim()}...`;
}

/* ── construção do documento ── */

const ALTURA_LINHA = mm(7);

/** Pool de imagens do documento — precisa ser preenchido antes de desenhar. */
class Recursos {
  constructor() {
    this.imagens = {};
    this.contador = 0;
  }

  registrar(imagem) {
    if (!imagem) return null;
    const nome = `Im${this.contador++}`;
    this.imagens[nome] = imagem;
    return nome;
  }
}

class Documento {
  constructor(config, recursos) {
    this.config = config;
    this.recursos = recursos;
    this.paginas = [];
    this.novaPagina(true);
  }

  novaPagina(primeira = false) {
    this.pagina = new Pagina();
    this.paginas.push(this.pagina);
    this.y = LAYOUT.topo;

    if (!primeira) return;

    const logo = this.config.logo;
    if (logo && logo.nome) {
      const altura = mm(9);
      const largura = Math.min(altura * (logo.largura / logo.altura), mm(50));
      this.pagina.imagem(logo.nome, LAYOUT.margemDireita - largura, this.y - mm(2), largura, altura);
    } else {
      this.pagina.textoDireita(LAYOUT.margemDireita, this.y, this.config.nomeLoja, {
        tamanho: 16,
        negrito: true,
      });
    }
  }

  espacoPara(altura) {
    if (this.y - altura < LAYOUT.base) this.novaPagina();
  }

  texto(conteudo, opcoes = {}) {
    const tamanho = opcoes.tamanho ?? 12;
    this.espacoPara(ALTURA_LINHA);
    this.pagina.texto(opcoes.x ?? LAYOUT.margemEsquerda, this.y, conteudo, { ...opcoes, tamanho });
    if (opcoes.mesmaLinha !== true) this.y -= opcoes.altura ?? ALTURA_LINHA;
  }

  regua(margemAntes = mm(3)) {
    this.y -= margemAntes;
    this.espacoPara(mm(2));
    this.pagina.linha(LAYOUT.linhaEsquerda, this.y, LAYOUT.linhaDireita, this.y);
    this.y -= mm(8);
  }

  pdf() {
    return montarPdf(this.paginas, this.recursos.imagens);
  }
}

/* ── seções ── */

function secaoCabecalho(doc, data) {
  doc.texto(`Orçamento ${data}`, { negrito: true });
  doc.regua(mm(1));
}

function secaoValidade(doc) {
  doc.texto('Validade da proposta:', { negrito: true, mesmaLinha: true });
  const x = LAYOUT.margemEsquerda + mm(55);
  const linhas = quebrarLinhas(
    'Os valores estão sujeitos a alterações, e promoções são válidas apenas no período vigente',
    12,
    false,
    LAYOUT.margemDireita - x
  );
  for (const linha of linhas) doc.texto(linha, { x, altura: mm(4.9) });
  doc.regua();
}

function secaoPagamento(doc, totalCentavos, config) {
  doc.texto('Formas de pagamento:', { negrito: true });
  doc.y -= mm(1);

  const totalPix = Math.round(totalCentavos * (1 - config.descontoPix / 100));
  doc.texto(`Pagamento no Pix: ${dinheiro(totalPix)} (${config.descontoPix}% de desconto)`, {
    altura: ALTURA_LINHA,
  });

  const totalCartao = Math.round(totalCentavos * (1 + config.jurosParcelamento / 100));
  const parcela = Math.round(totalCartao / config.parcelas);
  doc.texto(
    `Pagamento no Cartão: ${dinheiro(totalCentavos)} à vista ou até ${config.parcelas}x de ${dinheiro(parcela)}`,
    { altura: ALTURA_LINHA }
  );

  doc.regua();
}

function secaoContato(doc, config) {
  const temWhatsapp = Boolean(config.whatsappVisivel);
  const chamada = temWhatsapp
    ? 'Para continuar com o pedido ou tirar dúvidas, fale com o nosso atendimento pelo'
    : 'Para continuar com o pedido ou tirar dúvidas, fale com o nosso atendimento pelo site:';

  const linhas = quebrarLinhas(chamada, 12, true, LAYOUT.margemDireita - LAYOUT.margemEsquerda);
  for (const linha of linhas) doc.texto(linha, { negrito: true, altura: mm(5.5) });

  doc.y -= mm(2.5);
  doc.espacoPara(ALTURA_LINHA);

  if (!temWhatsapp) {
    doc.pagina.texto(LAYOUT.margemEsquerda, doc.y, config.dominio.replace(/^https?:\/\//, ''), {});
    doc.y -= mm(10);
    return;
  }

  doc.pagina.texto(LAYOUT.margemEsquerda, doc.y, 'whatsapp:', {});
  let x = LAYOUT.margemEsquerda + larguraTexto('whatsapp:', 12, false) + mm(3);
  if (config.nomeIconeWhatsapp) {
    doc.pagina.imagem(config.nomeIconeWhatsapp, x, doc.y - mm(1), mm(5), mm(5));
    x += mm(7);
  }
  doc.pagina.texto(x, doc.y, config.whatsappVisivel, {});
  doc.y -= mm(10);
}

function secaoQr(doc, config, legenda) {
  if (!config.nomeQr) return;
  const lado = mm(40);
  doc.espacoPara(lado + mm(10));
  const x = (PAGINA.largura - lado) / 2;
  doc.pagina.imagem(config.nomeQr, x, doc.y - lado, lado, lado);
  doc.y -= lado + mm(5);
  doc.pagina.texto((PAGINA.largura - larguraTexto(legenda, 9, false)) / 2, doc.y, legenda, {
    tamanho: 9,
    cinza: 0.35,
  });
}

/* ── download do arquivo ── */

function baixar(bytes, nomeArquivo) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function nomeArquivo(sufixo) {
  const agora = new Date();
  const data = [
    String(agora.getDate()).padStart(2, '0'),
    String(agora.getMonth() + 1).padStart(2, '0'),
    agora.getFullYear(),
  ].join('-');
  return `orcamento-miabeli-${sufixo}-${data}.pdf`;
}

/* ── preparação dos recursos comuns ── */

/**
 * Baixa e registra o que aparece em todo orçamento: logo, ícone do WhatsApp e QR.
 * Tudo é opcional — se qualquer um falhar, o PDF sai sem aquele elemento.
 */
async function prepararComuns(config, recursos, urlQr) {
  const [logo, icone, qr] = await Promise.all([
    obterJpeg(config.urlLogo, 500),
    svgParaJpeg(SVG_WHATSAPP, 96),
    urlQr ? gerarQrJpeg(urlQr, 320) : Promise.resolve(null),
  ]);

  config.logo = logo ? { ...logo, nome: recursos.registrar(logo) } : null;
  config.nomeIconeWhatsapp = recursos.registrar(icone);
  config.nomeQr = recursos.registrar(qr);
}

/* ── orçamento de um produto ── */

async function gerarOrcamentoProduto({ config, produto, variante, quantidade }) {
  const recursos = new Recursos();
  const titulo = variante.tituloCompleto || produto.titulo;

  // QR do orçamento de produto = conversa no WhatsApp com a mensagem pronta.
  // Quem está com a proposta na mão quer falar com alguém, não navegar de novo.
  // Sem número configurado, cai para o link do produto na loja.
  const linkQr = linkWhatsapp(
    config.whatsappDigitos,
    `Ola! Recebi o orcamento de: ${encurtar(titulo, 60)}. Quero continuar o pedido.`
  );
  const alvoQr = linkQr || `${config.dominio}${produto.url}?variant=${variante.id}`;
  const legendaQr = linkQr
    ? 'Aponte a câmera para falar com a gente no WhatsApp'
    : 'Aponte a câmera para abrir o produto na loja';

  const [, foto] = await Promise.all([
    prepararComuns(config, recursos, alvoQr),
    obterJpeg(variante.imagem, 700),
  ]);
  const nomeFoto = recursos.registrar(foto);

  const doc = new Documento(config, recursos);
  secaoCabecalho(doc, new Date().toLocaleDateString('pt-BR'));

  for (const linha of quebrarLinhas(titulo, 14, true, LAYOUT.margemDireita - LAYOUT.margemEsquerda)) {
    doc.texto(linha, { negrito: true, tamanho: 14, altura: mm(6) });
  }

  if (nomeFoto) {
    const lado = mm(50);
    doc.y -= mm(2);
    doc.espacoPara(lado);
    doc.pagina.imagem(nomeFoto, LAYOUT.margemEsquerda, doc.y - lado, lado, lado);
    doc.y -= lado;
  }
  doc.regua();

  secaoValidade(doc);

  const totalCentavos = variante.preco * quantidade;
  doc.texto(`Quantidade: ${quantidade}`, { negrito: true, mesmaLinha: true });
  doc.texto(`${dinheiro(variante.preco)} unit.`, { x: LAYOUT.margemEsquerda + mm(55), mesmaLinha: true });
  doc.texto(`${dinheiro(totalCentavos)} total`, { x: LAYOUT.margemEsquerda + mm(120) });
  doc.regua();

  secaoPagamento(doc, totalCentavos, config);
  secaoContato(doc, config);
  secaoQr(doc, config, legendaQr);

  baixar(doc.pdf(), nomeArquivo('produto'));
}

/* ── orçamento do carrinho ── */

async function gerarOrcamentoCarrinho({ config, carrinho }) {
  const recursos = new Recursos();
  const permalink = carrinho.items.map((item) => `${item.variant_id}:${item.quantity}`).join(',');

  const [, fotos] = await Promise.all([
    prepararComuns(config, recursos, `${config.dominio}/cart/${permalink}`),
    Promise.all(carrinho.items.map((item) => obterJpeg(urlFoto(item.image), 600))),
  ]);
  const nomesFotos = fotos.map((foto) => recursos.registrar(foto));

  const doc = new Documento(config, recursos);
  secaoCabecalho(doc, new Date().toLocaleDateString('pt-BR'));

  doc.texto('Itens do orçamento:', { negrito: true });
  doc.y -= mm(2);

  const ladoFoto = mm(18);
  const xTexto = LAYOUT.margemEsquerda + ladoFoto + mm(5);

  carrinho.items.forEach((item, indice) => {
    doc.espacoPara(ladoFoto + mm(6));
    const topo = doc.y;

    const nomeFoto = nomesFotos[indice];
    if (nomeFoto) doc.pagina.imagem(nomeFoto, LAYOUT.margemEsquerda, topo - ladoFoto, ladoFoto, ladoFoto);

    let yTexto = topo - mm(4);
    const larguraTitulo = LAYOUT.margemDireita - xTexto - mm(32);
    const linhas = quebrarLinhas(item.product_title || item.title, 11, true, larguraTitulo).slice(0, 2);
    for (const linha of linhas) {
      doc.pagina.texto(xTexto, yTexto, linha, { tamanho: 11, negrito: true });
      yTexto -= mm(5);
    }

    const variante = (item.variant_title || '').replace(/^Default Title$/i, '');
    if (variante) {
      doc.pagina.texto(xTexto, yTexto, variante, { tamanho: 10, cinza: 0.4 });
      yTexto -= mm(5);
    }

    doc.pagina.texto(xTexto, yTexto, `${item.quantity} × ${dinheiro(item.final_price)}`, {
      tamanho: 10,
      cinza: 0.3,
    });

    doc.pagina.textoDireita(LAYOUT.margemDireita, topo - mm(4), dinheiro(item.final_line_price), {
      tamanho: 11,
      negrito: true,
    });

    doc.y = Math.min(topo - ladoFoto, yTexto) - mm(4);
    doc.pagina.linha(LAYOUT.margemEsquerda, doc.y, LAYOUT.margemDireita, doc.y, {
      espessura: 0.4,
      cinza: 0.8,
    });
    doc.y -= mm(6);
  });

  doc.espacoPara(mm(12));
  doc.pagina.texto(LAYOUT.margemEsquerda, doc.y, 'Total do orçamento', { negrito: true, tamanho: 13 });
  doc.pagina.textoDireita(LAYOUT.margemDireita, doc.y, dinheiro(carrinho.total_price), {
    negrito: true,
    tamanho: 13,
  });
  doc.y -= ALTURA_LINHA;
  doc.regua();

  secaoValidade(doc);
  secaoPagamento(doc, carrinho.total_price, config);
  secaoContato(doc, config);
  secaoQr(doc, config, 'Aponte a câmera para reabrir esta sacola na loja');

  baixar(doc.pdf(), nomeArquivo('sacola'));
}

function urlFoto(url) {
  if (!url) return null;
  // A CDN da Shopify redimensiona por query param. O sufixo legado (_600x) só
  // vale mantendo a extensão original, então dá 404 em .png. O format=jpg é
  // ignorado quando o arquivo tem transparência — aí o obterJpeg rasteriza.
  return `${url}${url.includes('?') ? '&' : '?'}width=600&format=jpg`;
}

/* ── ligação ao DOM ── */

function lerConfig(elemento) {
  const bruto = JSON.parse(elemento.querySelector('[data-orcamento-config]').textContent);
  return {
    nomeLoja: bruto.nomeLoja,
    urlLogo: bruto.urlLogo || null,
    whatsappVisivel: formatarWhatsapp(bruto.whatsapp),
    whatsappDigitos: String(bruto.whatsapp || '').replace(/\D/g, ''),
    descontoPix: Number(bruto.descontoPix) || 5,
    jurosParcelamento: Number(bruto.jurosParcelamento) || 0,
    parcelas: Number(bruto.parcelas) || 12,
    dominio: bruto.dominio,
  };
}

async function comEstadoDeCarga(botao, tarefa) {
  if (botao.dataset.ocupado === '1') return;
  botao.dataset.ocupado = '1';
  const original = botao.querySelector('[data-orcamento-texto]');
  const textoOriginal = original ? original.textContent : null;
  if (original) original.textContent = 'Gerando…';
  botao.setAttribute('aria-busy', 'true');

  try {
    await tarefa();
  } catch (erro) {
    console.error('[orçamento] falha ao gerar o PDF', erro);
    if (original) original.textContent = 'Erro ao gerar';
    setTimeout(() => {
      if (original && textoOriginal) original.textContent = textoOriginal;
    }, 2500);
    botao.removeAttribute('aria-busy');
    delete botao.dataset.ocupado;
    return;
  }

  if (original && textoOriginal) original.textContent = textoOriginal;
  botao.removeAttribute('aria-busy');
  delete botao.dataset.ocupado;
}

document.addEventListener('click', (evento) => {
  const botao = evento.target.closest('[data-orcamento-produto], [data-orcamento-carrinho]');
  if (!botao) return;
  evento.preventDefault();

  const raiz = botao.closest('[data-orcamento-raiz]');
  if (!raiz) return;
  const config = lerConfig(raiz);

  if (botao.matches('[data-orcamento-produto]')) {
    const produto = JSON.parse(raiz.querySelector('[data-orcamento-produto-dados]').textContent);

    comEstadoDeCarga(botao, async () => {
      // A variante e a quantidade vêm do formulário de compra, não do Liquid:
      // assim o orçamento acompanha o que o cliente escolheu na página.
      const formulario = document.querySelector('product-form-component form, form[action*="/cart/add"]');
      const idSelecionado = formulario?.querySelector('[name="id"]')?.value;
      const variante = produto.variantes[idSelecionado] || produto.variantes[produto.varianteInicial];
      if (!variante) throw new Error('variante não encontrada');

      const quantidade = Math.max(1, parseInt(formulario?.querySelector('[name="quantity"]')?.value, 10) || 1);
      await gerarOrcamentoProduto({ config, produto, variante, quantidade });
    });
    return;
  }

  comEstadoDeCarga(botao, async () => {
    const resposta = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    const carrinho = await resposta.json();
    if (!carrinho.items || carrinho.items.length === 0) throw new Error('sacola vazia');

    await gerarOrcamentoCarrinho({ config, carrinho });
  });
});
